import React, { useState, useEffect, useMemo } from "react";
import {
  collectFeePayment,
  collectFeePaymentPortal,
  listPaymentModes,
  listPaymentModesPortal,
} from "../../api/fees/feeApi";
import {
  Button,
  Input,
  Select,
  Textarea,
  Modal,
} from "../../components/ui";
import { useToast } from "../../context/ToastContext";

const FALLBACK_MODES = [
  { code: "CASH", displayName: "Cash", requiresTransactionId: false },
  { code: "UPI", displayName: "UPI", requiresTransactionId: true },
  { code: "CARD", displayName: "Card", requiresTransactionId: true },
  { code: "CHEQUE", displayName: "Cheque", requiresTransactionId: true },
  { code: "ONLINE", displayName: "Online", requiresTransactionId: true },
];

const CollectFeeModal = ({
  isOpen,
  onClose,
  onSuccess,
  student,
  summary,
  academicYearId,
  isPortal = false,
}) => {
  const [form, setForm] = useState({
    totalPaid: "",
    paymentMode: "CASH",
    transactionId: "",
    remarks: "",
  });
  const [modes, setModes] = useState(FALLBACK_MODES);
  const [loading, setLoading] = useState(false);
  const toast = useToast();

  useEffect(() => {
    if (!isOpen) return;
    const outstanding = Number(summary?.totalOutstanding || 0);
    setForm({
      totalPaid: outstanding > 0 ? outstanding.toFixed(2) : "",
      paymentMode: "CASH",
      transactionId: "",
      remarks: "",
    });
    (async () => {
      try {
        const res = isPortal
          ? await listPaymentModesPortal({ isActive: true })
          : await listPaymentModes({ isActive: true });
        const list = (res.data || res || []).filter((m) => m.isActive !== false);
        if (list.length) {
          setModes(list);
          setForm((p) => ({ ...p, paymentMode: list[0].code || list[0].displayName }));
        }
      } catch {
        setModes(FALLBACK_MODES);
      }
    })();
  }, [isOpen, summary, isPortal]);

  const selectedMode = useMemo(() => {
    return modes.find(
      (m) => m.code === form.paymentMode || m.displayName === form.paymentMode
    );
  }, [modes, form.paymentMode]);

  const modeOptions = useMemo(() => {
    return modes.map((m) => ({
      label: m.displayName || m.code,
      value: m.code || m.displayName,
    }));
  }, [modes]);

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (!student?._id || !academicYearId) {
      toast.error("Student and academic year are required.");
      return;
    }
    if (!form.totalPaid || Number(form.totalPaid) <= 0) {
      toast.error("Enter a valid amount.");
      return;
    }
    if (selectedMode?.requiresTransactionId && !form.transactionId.trim()) {
      toast.error("Transaction ID is required for this payment mode.");
      return;
    }
    setLoading(true);
    try {
      const payload = {
        studentId: student._id,
        academicYearId,
        totalPaid: Number(form.totalPaid),
        paymentMode: form.paymentMode,
        transactionId: form.transactionId || "",
        remarks: form.remarks || "",
      };
      const res = isPortal
        ? await collectFeePaymentPortal(payload)
        : await collectFeePayment(payload);
      onSuccess(res.data || res);
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.message || "Payment failed.");
    } finally {
      setLoading(false);
    }
  };

  const studentName = useMemo(() => {
    if (!student) return "";
    return [student.firstName, student.middleName, student.lastName]
      .filter(Boolean)
      .join(" ");
  }, [student]);

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Collect Payment"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={loading}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleSubmit}
            isLoading={loading}
          >
            Collect
          </Button>
        </>
      }
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        

        {/* Student Details Context Header */}
        <div className="rounded-lg border border-neutral-200 dark:border-neutral-800 bg-neutral-50/60 p-3 flex items-center justify-between">
          <div>
            <p className="text-xs text-neutral-500 dark:text-neutral-400 font-medium">Student</p>
            <p className="text-sm font-semibold text-neutral-900 dark:text-neutral-100">{studentName || "—"}</p>
          </div>
          <div className="text-right">
            <p className="text-xs text-neutral-500 dark:text-neutral-400 font-medium">Outstanding</p>
            <p className="text-sm font-semibold text-danger-500">
              ₹{Number(summary?.totalOutstanding || 0).toFixed(2)}
            </p>
          </div>
        </div>

        <Input
          label="Amount *"
          type="number"
          min="0.01"
          step="0.01"
          value={form.totalPaid}
          onChange={(e) => setForm({ ...form, totalPaid: e.target.value })}
          required
        />

        <Select
          label="Payment mode"
          options={modeOptions}
          value={form.paymentMode}
          onChange={(val) => setForm({ ...form, paymentMode: val })}
        />

        <Input
          label={`Transaction ID${selectedMode?.requiresTransactionId ? " *" : ""}`}
          type="text"
          value={form.transactionId}
          onChange={(e) => setForm({ ...form, transactionId: e.target.value })}
          placeholder="e.g. TXN12345678"
        />

        <Textarea
          label="Remarks"
          value={form.remarks}
          onChange={(e) => setForm({ ...form, remarks: e.target.value })}
          rows={2}
          placeholder="Optional notes or reference info"
        />
      </form>
    </Modal>
  );
};

export default CollectFeeModal;